/* ------------------------ Functional imports start ------------------------ */
import { createContext, useEffect, useState } from 'react'

import LogTool from '../../logger/logTools'
import { useUserContext } from '../../utils/context'
import { isPrivileged } from '../../utils/functions'
import { useUnreadFeedbackSubmissionCount } from './hooks/useSharepointQueries'
/* ------------------------- Functional imports end ------------------------- */

type UnreadFeedbackSubmissionCountObject = {
  total: number
  articleShares: number
  BOMShares: number
  guestRequestShares: number
  requestShares: number
}

type ShareTypes = 'articleShares' | 'BOMShares' | 'guestRequestShares' | 'requestShares'

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function SharepointGlobalStateProvider(props: { children: React.ReactNode }) {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'SharepointGlobalStates', enable: true, logLevel: 'debug' })
  const { user } = useUserContext()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- React Query Hooks --------------------------- */
  // Only fetch feedback count if user has appropriate permissions
  // This requires Items module access and proper CRUD/Admin permissions on the backend
  const shouldFetchFeedback = Boolean(user) && (
    isPrivileged(user, 'STAFF') || 
    isPrivileged(user, 'SUPERUSER') || 
    isPrivileged(user, 'ORGADMIN')
  )
  const { data: fetchedFeedbackCount } = useUnreadFeedbackSubmissionCount(shouldFetchFeedback)
  /* ---------------------------- React Query Hooks End ----------------------- */

  /* ---------------------------- Data states start --------------------------- */
  // note: a value of undefined indicates that the feedbackSubmissionCount was not fetched yet
  const [unreadFeedbackSubmissionCount, setUnreadFeedbackSubmissionCount] = useState<
    UnreadFeedbackSubmissionCountObject | undefined
  >()
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    // Update local state when data is fetched
    if (fetchedFeedbackCount) {
      log.info('Success fetching unread feedback submission count', fetchedFeedbackCount)
      setUnreadFeedbackSubmissionCount(fetchedFeedbackCount)
    }
  }, [fetchedFeedbackCount])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */

  function addToFeedbackCount(value: number, shareType: ShareTypes) {
    if (typeof unreadFeedbackSubmissionCount === 'undefined') return

    setUnreadFeedbackSubmissionCount(prev => {
      prev!.total += value
      prev![shareType] += value
      return { ...prev! }
    })
  }
  function subtractFromFeedbackCount(value: number, shareType: ShareTypes) {
    if (typeof unreadFeedbackSubmissionCount === 'undefined') return

    setUnreadFeedbackSubmissionCount(prev => {
      prev!.total -= value
      prev![shareType] -= value
      return { ...prev! }
    })
  }
  function setFeedbackCount(value: number, shareType: ShareTypes) {
    if (typeof unreadFeedbackSubmissionCount === 'undefined') return

    setUnreadFeedbackSubmissionCount(prev => {
      const valueDifference = prev![shareType] - value
      prev!.total += valueDifference
      prev![shareType] = value
      return { ...prev! }
    })
  }
  /* -------------------------- Utility functions end ------------------------- */

  log.debug('unreadFeedbackSubmissionsCount ->', unreadFeedbackSubmissionCount)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <SharepointGlobalStates.Provider
      value={{
        unreadFeedbackSubmissionCount: unreadFeedbackSubmissionCount || {
          total: 0,
          articleShares: 0,
          BOMShares: 0,
          guestRequestShares: 0,
          requestShares: 0,
        },
        addToFeedbackCount: addToFeedbackCount,
        subtractFromFeedbackCount: subtractFromFeedbackCount,
        setFeedbackCount: setFeedbackCount,
      }}
    >
      {props.children}
    </SharepointGlobalStates.Provider>
  )
}

interface ISharepointGlobalStates {
  unreadFeedbackSubmissionCount: UnreadFeedbackSubmissionCountObject
  // setUnreadFeedbackSubmissionCount: React.Dispatch<React.SetStateAction<UnreadFeedbackSubmissionCountObject>>
  addToFeedbackCount: (value: number, shareType: ShareTypes) => void
  subtractFromFeedbackCount: (value: number, shareType: ShareTypes) => void
  setFeedbackCount: (value: number, shareType: ShareTypes) => void
}

export const SharepointGlobalStates = createContext<ISharepointGlobalStates>({
  unreadFeedbackSubmissionCount: {
    total: 0,
    articleShares: 0,
    BOMShares: 0,
    guestRequestShares: 0,
    requestShares: 0,
  },
  addToFeedbackCount: (value: number, shareType: ShareTypes) => null,
  subtractFromFeedbackCount: (value: number, shareType: ShareTypes) => null,
  setFeedbackCount: (value: number, shareType: ShareTypes) => null,
})
